import * as Yup from 'yup';

// Schema de validação para os alertas seguindo IndicatorAlertConfiguration
export const alertValidationSchema = Yup.object().shape({
  // Schema sempre válido - não bloqueia o formulário
  // A validação real acontece apenas nos campos modificados
});

export const generiAlertValidationSchema = Yup.object().shape({
  type: Yup.string()
    .oneOf(['quota', 'fund', 'wallet', 'default'])
    .default('fund')
    .nullable(),
  entityId: Yup.number().nullable(),
  indicator: Yup.string().nullable(),
  startValue: Yup.number().required('Campo obrigatório'),
  endValue: Yup.number().when('operator', {
    is: 'between',
    then: (schema) =>
      schema
        .required('Valor final é obrigatório para operador "Entre"')
        .test(
          'end-greater-than-start',
          'Valor final deve ser maior que o valor inicial',
          function (value) {
            if (value === undefined || value === null) return true;
            const { parent } = this;
            if (parent?.startValue && value <= parent.startValue) {
              return false;
            }
            return true;
          },
        ),
    otherwise: (schema) =>
      schema.nullable().transform((value) => (value === 0 ? null : value)),
  }),
  operator: Yup.string().required('Campo obrigatório'),
  criticality: Yup.string().required('Campo obrigatório'),
  isActive: Yup.boolean().required('Campo obrigatório'),
  email: Yup.boolean().required('Campo obrigatório'),
  tabType: Yup.string().nullable(),
});

// Schema para validação condicional - só valida campos modificados
export const formikAlertValidationSchema = Yup.object().shape({
  indicatorsWithAlerts: Yup.array().of(
    Yup.object().shape({
      key: Yup.string().required(),
      label: Yup.string().required(),
      alerts: Yup.array().of(
        Yup.object().shape({
          id: Yup.number().nullable(),
          internalId: Yup.string().nullable(),
          type: Yup.string()
            .oneOf(['quota', 'fund', 'wallet', 'default'])
            .default('fund')
            .nullable(),
          entityId: Yup.number().nullable(),
          indicator: Yup.string().nullable(),
          operator: Yup.string().required('Campo obrigatório'),
          startValue: Yup.number().required('Campo obrigatório'),
          endValue: Yup.number().when('operator', {
            is: 'between',
            then: (schema) =>
              schema
                .required('Campo obrigatório')
                .test(
                  'end-greater-than-start',
                  'Valor final deve ser maior que o inicial',
                  function (value) {
                    if (value === undefined || value === null) return true;
                    const { parent } = this;
                    if (parent?.startValue && value <= parent.startValue) {
                      return false;
                    }
                    return true;
                  },
                ),
            otherwise: (schema) =>
              schema
                .nullable()
                .transform((value) => (value === 0 ? null : value)),
          }),
          criticality: Yup.string().required('Campo obrigatório'),
          isActive: Yup.boolean().required('Campo obrigatório'),
          email: Yup.boolean().required('Campo obrigatório'),
          tabType: Yup.string().nullable(),
        }),
      ),
    }),
  ),
});

// Schema com array único para ambas as abas
export const unifiedAlertValidationSchema = Yup.object().shape({
  allAlerts: Yup.array().of(
    Yup.object().shape({
      ...generiAlertValidationSchema.fields,
    }),
  ),
  metadata: Yup.object().shape({
    walletId: Yup.string().required(),
    fundId: Yup.string().required(),
    selectedTab: Yup.string().oneOf(['percentage', 'amount']).required(),
  }),
});

export type AlertValidationSchema = Yup.InferType<
  typeof formikAlertValidationSchema
>;

// Validação individual para cada configuração de alerta
export const individualAlertSchema = Yup.object().shape({
  type: Yup.string()
    .oneOf(['default', 'wallet', 'fund', 'quota'], 'Tipo inválido')
    .required('Tipo é obrigatório'),
  entityId: Yup.number().required('ID da entidade é obrigatório'),
  indicator: Yup.string().required('Indicador é obrigatório'),
  startValue: Yup.number().required('Valor inicial é obrigatório'),
  endValue: Yup.number().when('operator', {
    is: 'between',
    then: (schema) =>
      schema
        .required('Valor final é obrigatório para operador "Entre"')
        .test(
          'end-greater-than-start',
          'Valor final deve ser maior que o inicial',
          function (value) {
            if (value === undefined || value === null) return true;
            const { parent } = this;
            if (parent?.startValue && value <= parent.startValue) {
              return this.createError({
                message: 'Valor final deve ser maior que o inicial',
              });
            }
            return true;
          },
        ),
    otherwise: (schema) =>
      schema.nullable().transform((value) => (value === 0 ? null : value)),
  }),
  operator: Yup.string().required('Operador é obrigatório'),
  criticality: Yup.string().required('Criticidade do alerta é obrigatória'),
  isActive: Yup.boolean().required('Status ativo é obrigatório'),
  email: Yup.boolean().required('Status email é obrigatório'),
});

// Schema para validação de operações CRUD
export const crudAlertValidationSchema = Yup.object().shape({
  toCreate: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().nullable(),
      ...generiAlertValidationSchema.fields,
    }),
  ),
  toUpdate: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().required('ID é obrigatório'),
      ...generiAlertValidationSchema.fields,
    }),
  ),
  toDelete: Yup.array().of(
    Yup.object().shape({
      id: Yup.number().required('ID é obrigatório'),
      ...generiAlertValidationSchema.fields,
    }),
  ),
  metadata: Yup.object().shape({
    walletId: Yup.string().required('ID da carteira é obrigatório'),
    fundId: Yup.string().required('ID do fundo é obrigatório'),
  }),
});
